import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Divider,
  Alert,
  Snackbar,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { ChromePicker } from 'react-color';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import PreviewIcon from '@mui/icons-material/Preview';
import InfoIcon from '@mui/icons-material/Info';
import { apiService } from '../services/api.service';
import { useTheme } from '../theme/ThemeProvider';
import { useTenant } from '../context/TenantContext';

// Sample components to preview theme
import SampleComponents from './SampleComponents';

// TabPanel component for the tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`theme-tabpanel-${index}`}
      aria-labelledby={`theme-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// ColorPicker component
function ColorPicker({ label, color, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          onClick={() => setShowPicker(!showPicker)}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            backgroundColor: color,
            cursor: 'pointer',
            border: '1px solid #ccc',
            mr: 2
          }}
        />
        <TextField
          size="small"
          value={color}
          onChange={(e) => onChange(e.target.value)}
          sx={{ width: 120 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Click the color box to open color picker">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {showPicker && (
        <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
          <Box
            sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
            onClick={() => setShowPicker(false)}
          />
          <ChromePicker
            color={color}
            onChange={(color) => onChange(color.hex)}
          />
        </Box>
      )}
    </Box>
  );
}

const fontPresets = [
  {
    name: 'Modern Professional',
    value: "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif"
  },
  {
    name: 'Corporate Clean',
    value: "'Helvetica Neue', 'Arial', 'Roboto', sans-serif"
  },
  {
    name: 'Contemporary',
    value: "'Poppins', 'Montserrat', 'Arial', sans-serif"
  },
  {
    name: 'System Native',
    value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', sans-serif"
  }
];

// Main ThemeCustomizer component
function ThemeCustomizer() {
  const { tenantAlias } = useParams();
  const navigate = useNavigate();
  const { theme: currentTheme, themeConfig, loading: themeLoading } = useTheme();
  const { tenantAlias: contextTenantAlias, isLoading: isTenantLoading } = useTenant();
  
  const [activeTab, setActiveTab] = useState(0);
  const [themeData, setThemeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [tenantInfo, setTenantInfo] = useState(null);
  
  // Fetch theme data when component mounts
  useEffect(() => {
    const fetchThemeData = async () => {
      try {
        setLoading(true);
        
        // Validate that we have a tenant alias
        const effectiveTenantAlias = tenantAlias || contextTenantAlias;
        
        if (!effectiveTenantAlias) {
          setNotification({
            open: true,
            message: 'No tenant alias provided. Please specify a tenant.',
            severity: 'error'
          });
          setLoading(false);
          return;
        }
        
        console.log(`Fetching theme for tenant: ${effectiveTenantAlias}`);
        
        // Fetch tenant info
        try {
          const tenantResponse = await apiService.get(`/admin/developer/tenant/${effectiveTenantAlias}`);
          setTenantInfo(tenantResponse);
        } catch (tenantError) {
          console.error('Failed to load tenant info:', tenantError);
          setNotification({
            open: true,
            message: 'Failed to load tenant information',
            severity: 'warning'
          });
        }
        
        // Fetch theme data
        try {
          const themeResponse = await apiService.get(`/ux/themes/${effectiveTenantAlias}`);
          
          if (themeResponse && Object.keys(themeResponse).length > 0) {
            console.log('Theme loaded successfully:', themeResponse);
            setThemeData(themeResponse);
          } else {
            // If no theme found, use current theme config
            console.log('No theme found, using current theme config');
            setThemeData(themeConfig);
          }
        } catch (themeError) {
          console.error('Failed to load theme, using default:', themeError);
          setThemeData(themeConfig);
          setNotification({
            open: true,
            message: 'Failed to load theme. Using default theme as starting point.',
            severity: 'warning'
          });
        }
      } catch (error) {
        console.error('Error in theme customizer:', error);
        setNotification({
          open: true,
          message: 'An error occurred while loading the theme customizer',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch when tenant alias is available (either from URL or context)
    if (!isTenantLoading && (tenantAlias || contextTenantAlias)) {
      fetchThemeData();
    }
  }, [tenantAlias, contextTenantAlias, isTenantLoading, themeConfig]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Update theme data
  const updateThemeData = (path, value) => {
    setThemeData(prevData => {
      const newData = { ...prevData };
      const pathParts = path.split('.');
      
      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = {};
        }
        current = current[pathParts[i]];
      }
      
      current[pathParts[pathParts.length - 1]] = value;
      return newData;
    });
  };
  
  // Save theme
  const saveTheme = async () => {
    try {
      setSaving(true);
      
      const effectiveTenantAlias = tenantAlias || contextTenantAlias;
      
      if (!effectiveTenantAlias) {
        setNotification({
          open: true,
          message: 'No tenant alias provided. Cannot save theme.',
          severity: 'error'
        });
        setSaving(false);
        return;
      }
      
      console.log(`Saving theme for tenant: ${effectiveTenantAlias}`);
      console.log('Theme data to save:', themeData);
      
      // Save theme using the API
      await apiService.post(`/ux/themes/${effectiveTenantAlias}`, themeData);
      
      setNotification({
        open: true,
        message: 'Theme saved successfully',
        severity: 'success'
      });
      
      // Refresh the page to apply the new theme
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Failed to save theme:', error);
      setNotification({
        open: true,
        message: 'Failed to save theme: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Reset theme to default
  const resetTheme = async () => {
    try {
      setSaving(true);
      
      const effectiveTenantAlias = tenantAlias || contextTenantAlias;
      
      if (!effectiveTenantAlias) {
        setNotification({
          open: true,
          message: 'No tenant alias provided. Cannot reset theme.',
          severity: 'error'
        });
        setSaving(false);
        return;
      }
      
      // Reset theme using the API
      await apiService.delete(`/ux/themes/${effectiveTenantAlias}`);
      
      setNotification({
        open: true,
        message: 'Theme reset to default successfully',
        severity: 'success'
      });
      
      // Refresh the page to apply the default theme
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Failed to reset theme:', error);
      setNotification({
        open: true,
        message: 'Failed to reset theme: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Close notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };
  
  // If loading, show loading indicator
  if (loading || isTenantLoading || themeLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Theme Customizer...
        </Typography>
      </Box>
    );
  }
  
  // If no theme data, show error
  if (!themeData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load theme data. Please try again later.
        </Alert>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => navigate('/developers')}
        >
          Back to Developer Dashboard
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Theme Customizer
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Customize the theme for tenant: <strong>{tenantAlias || contextTenantAlias}</strong>
            {tenantInfo && tenantInfo.name && ` (${tenantInfo.name})`}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={resetTheme}
            sx={{ mr: 1 }}
            disabled={saving}
          >
            Reset to Default
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveTheme}
            disabled={saving}
            color="primary"
          >
            {saving ? <CircularProgress size={24} /> : 'Save Theme'}
          </Button>
        </Box>
      </Box>
      
      {/* Preview toggle */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={previewMode}
              onChange={(e) => setPreviewMode(e.target.checked)}
              color="primary"
            />
          }
          label="Preview Mode"
        />
      </Box>
      
      <Grid container spacing={3}>
        {/* Theme editor */}
        <Grid item xs={12} md={previewMode ? 6 : 12}>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Colors" />
              <Tab label="Typography" />
              <Tab label="Components" />
              <Tab label="Custom" />
              <Tab label="Advanced" />
            </Tabs>
            
            {/* Colors Tab */}
            <TabPanel value={activeTab} index={0}>
              <Typography variant="h6" gutterBottom>Primary Colors</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Primary Main"
                    color={themeData.palette?.primary?.main || '#1976d2'}
                    onChange={(value) => updateThemeData('palette.primary.main', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Primary Dark"
                    color={themeData.palette?.primary?.dark || '#1565c0'}
                    onChange={(value) => updateThemeData('palette.primary.dark', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Primary Light"
                    color={themeData.palette?.primary?.light || '#42a5f5'}
                    onChange={(value) => updateThemeData('palette.primary.light', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Primary Contrast Text"
                    color={themeData.palette?.primary?.contrastText || '#ffffff'}
                    onChange={(value) => updateThemeData('palette.primary.contrastText', value)}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Secondary Colors</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Secondary Main"
                    color={themeData.palette?.secondary?.main || '#dc004e'}
                    onChange={(value) => updateThemeData('palette.secondary.main', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Secondary Dark"
                    color={themeData.palette?.secondary?.dark || '#9a0036'}
                    onChange={(value) => updateThemeData('palette.secondary.dark', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Secondary Light"
                    color={themeData.palette?.secondary?.light || '#ff4081'}
                    onChange={(value) => updateThemeData('palette.secondary.light', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Secondary Contrast Text"
                    color={themeData.palette?.secondary?.contrastText || '#ffffff'}
                    onChange={(value) => updateThemeData('palette.secondary.contrastText', value)}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Background Colors</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Background Default"
                    color={themeData.palette?.background?.default || '#f5f5f5'}
                    onChange={(value) => updateThemeData('palette.background.default', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Background Paper"
                    color={themeData.palette?.background?.paper || '#ffffff'}
                    onChange={(value) => updateThemeData('palette.background.paper', value)}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Text Colors</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Text Primary"
                    color={themeData.palette?.text?.primary || '#333333'}
                    onChange={(value) => updateThemeData('palette.text.primary', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Text Secondary"
                    color={themeData.palette?.text?.secondary || '#666666'}
                    onChange={(value) => updateThemeData('palette.text.secondary', value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ColorPicker
                    label="Text Disabled"
                    color={themeData.palette?.text?.disabled || '#999999'}
                    onChange={(value) => updateThemeData('palette.text.disabled', value)}
                  />
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Typography Tab */}
            <TabPanel value={activeTab} index={1}>
              <Typography variant="h6" gutterBottom>Font Family</Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Font Family</InputLabel>
                <Select
                  value={themeData.typography?.fontFamily || fontPresets[0].value}
                  onChange={(e) => updateThemeData('typography.fontFamily', e.target.value)}
                >
                  {fontPresets.map((preset) => (
                    <MenuItem key={preset.name} value={preset.value}>
                      {preset.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
                {themeData.typography?.fontFamily === 'custom' && (
                  <TextField
                    fullWidth
                    label="Custom Font Family"
                    placeholder="'Your Font', 'Fallback Font', sans-serif"
                    onChange={(e) => updateThemeData('typography.fontFamily', e.target.value)}
                    helperText="Enter comma-separated font families"
                    margin="normal"
                  />
                )}
              </FormControl>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Headings</Typography>
              <Grid container spacing={2}>
                {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((heading) => (
                  <Grid item xs={12} md={6} key={heading}>
                    <Typography variant="subtitle1" gutterBottom>
                      {heading.toUpperCase()}
                    </Typography>
                    <TextField
                      label="Font Size"
                      value={themeData.typography?.[heading]?.fontSize || ''}
                      onChange={(e) => updateThemeData(`typography.${heading}.fontSize`, e.target.value)}
                      helperText="Example: 2rem, 24px"
                      margin="dense"
                      fullWidth
                    />
                    <TextField
                      label="Font Weight"
                      value={themeData.typography?.[heading]?.fontWeight || ''}
                      onChange={(e) => updateThemeData(`typography.${heading}.fontWeight`, e.target.value)}
                      helperText="Example: 400, 500, 600, 700"
                      margin="dense"
                      fullWidth
                    />
                    <TextField
                      label="Line Height"
                      value={themeData.typography?.[heading]?.lineHeight || ''}
                      onChange={(e) => updateThemeData(`typography.${heading}.lineHeight`, e.target.value)}
                      helperText="Example: 1.2, 1.5"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Body Text</Typography>
              <Grid container spacing={2}>
                {['body1', 'body2', 'button', 'caption', 'overline'].map((textType) => (
                  <Grid item xs={12} md={6} key={textType}>
                    <Typography variant="subtitle1" gutterBottom>
                      {textType.charAt(0).toUpperCase() + textType.slice(1)}
                    </Typography>
                    <TextField
                      label="Font Size"
                      value={themeData.typography?.[textType]?.fontSize || ''}
                      onChange={(e) => updateThemeData(`typography.${textType}.fontSize`, e.target.value)}
                      helperText="Example: 1rem, 16px"
                      margin="dense"
                      fullWidth
                    />
                    <TextField
                      label="Font Weight"
                      value={themeData.typography?.[textType]?.fontWeight || ''}
                      onChange={(e) => updateThemeData(`typography.${textType}.fontWeight`, e.target.value)}
                      helperText="Example: 400, 500"
                      margin="dense"
                      fullWidth
                    />
                    <TextField
                      label="Line Height"
                      value={themeData.typography?.[textType]?.lineHeight || ''}
                      onChange={(e) => updateThemeData(`typography.${textType}.lineHeight`, e.target.value)}
                      helperText="Example: 1.5"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </TabPanel>
            
            {/* Components Tab */}
            <TabPanel value={activeTab} index={2}>
              <Typography variant="h6" gutterBottom>Shape</Typography>
              <TextField
                label="Border Radius"
                type="number"
                value={themeData.shape?.borderRadius || 4}
                onChange={(e) => updateThemeData('shape.borderRadius', Number(e.target.value))}
                helperText="Border radius in pixels for components"
                margin="normal"
                InputProps={{ inputProps: { min: 0 } }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Spacing</Typography>
              <TextField
                label="Base Spacing Unit"
                type="number"
                value={themeData.spacing || 8}
                onChange={(e) => updateThemeData('spacing', Number(e.target.value))}
                helperText="Base spacing unit in pixels"
                margin="normal"
                InputProps={{ inputProps: { min: 1 } }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" gutterBottom>Component Overrides</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Component overrides allow you to customize specific MUI components.
                This is an advanced feature that requires knowledge of MUI's styling system.
              </Alert>
              
              <Typography variant="subtitle1" gutterBottom>Button</Typography>
              <TextField
                label="Button Text Transform"
                select
                value={themeData.components?.MuiButton?.styleOverrides?.root?.textTransform || 'none'}
                onChange={(e) => updateThemeData('components.MuiButton.styleOverrides.root.textTransform', e.target.value)}
                helperText="Text transformation for buttons"
                margin="normal"
                fullWidth
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="uppercase">Uppercase</MenuItem>
                <MenuItem value="lowercase">Lowercase</MenuItem>
                <MenuItem value="capitalize">Capitalize</MenuItem>
              </TextField>
              
              <TextField
                label="Button Font Weight"
                value={themeData.components?.MuiButton?.styleOverrides?.root?.fontWeight || ''}
                onChange={(e) => updateThemeData('components.MuiButton.styleOverrides.root.fontWeight', e.target.value)}
                helperText="Font weight for buttons"
                margin="normal"
                fullWidth
              />
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Card</Typography>
              <TextField
                label="Card Border Radius"
                value={themeData.components?.MuiCard?.styleOverrides?.root?.borderRadius || ''}
                onChange={(e) => updateThemeData('components.MuiCard.styleOverrides.root.borderRadius', e.target.value)}
                helperText="Border radius for cards (e.g., '12px')"
                margin="normal"
                fullWidth
              />
              
              <TextField
                label="Card Box Shadow"
                value={themeData.components?.MuiCard?.styleOverrides?.root?.boxShadow || ''}
                onChange={(e) => updateThemeData('components.MuiCard.styleOverrides.root.boxShadow', e.target.value)}
                helperText="Box shadow for cards (e.g., '0px 2px 8px rgba(0, 0, 0, 0.05)')"
                margin="normal"
                fullWidth
              />
            </TabPanel>
            
            {/* Custom Tab */}
            <TabPanel value={activeTab} index={3}>
              <Typography variant="h6" gutterBottom>Custom Theme Properties</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                These custom properties can be used throughout the application for consistent branding.
              </Alert>
              
              <TextField
                label="Application Name"
                value={themeData.custom?.appName || 'Supply Chain Management'}
                onChange={(e) => updateThemeData('custom.appName', e.target.value)}
                helperText="The name of the application shown in the header"
                margin="normal"
                fullWidth
              />
              
              <TextField
                label="Footer Text"
                value={themeData.custom?.footerText || '© 2023 Supply Chain Inc. All rights reserved.'}
                onChange={(e) => updateThemeData('custom.footerText', e.target.value)}
                helperText="Text displayed in the footer"
                margin="normal"
                fullWidth
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Navbar Position</InputLabel>
                <Select
                  value={themeData.custom?.navbarPosition || 'left'}
                  onChange={(e) => updateThemeData('custom.navbarPosition', e.target.value)}
                  label="Navbar Position"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="top">Top</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Dashboard Layout</InputLabel>
                <Select
                  value={themeData.custom?.dashboardLayout || 'standard'}
                  onChange={(e) => updateThemeData('custom.dashboardLayout', e.target.value)}
                  label="Dashboard Layout"
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                  <MenuItem value="expanded">Expanded</MenuItem>
                </Select>
              </FormControl>
            </TabPanel>
            
            {/* Advanced Tab */}
            <TabPanel value={activeTab} index={4}>
              <Typography variant="h6" gutterBottom>Advanced Theme Configuration</Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is an advanced feature. Incorrect JSON can break your theme.
                Make sure your JSON is valid before saving.
              </Alert>
              
              <TextField
                label="Raw Theme JSON"
                multiline
                rows={20}
                value={JSON.stringify(themeData, null, 2)}
                onChange={(e) => {
                  try {
                    const parsedJson = JSON.parse(e.target.value);
                    setThemeData(parsedJson);
                  } catch (error) {
                    // Don't update if JSON is invalid
                    console.error('Invalid JSON:', error);
                  }
                }}
                fullWidth
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            </TabPanel>
          </Paper>
        </Grid>
        
        {/* Preview area */}
        {previewMode && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%', overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Theme Preview
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <SampleComponents themeData={themeData} />
            </Paper>
          </Grid>
        )}
      </Grid>
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ThemeCustomizer; 